import React, { useMemo, useState } from "react";

import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
} from "react-bootstrap";

import { useCart } from "../context/CartContext";
import PayButton from "../components/PayButton";

import {
  FaTrash,
  FaMinus,
  FaPlus,
  FaShoppingCart,
  FaArrowLeft,
  FaShieldAlt,
  FaTruck,
  FaCreditCard,
} from "react-icons/fa";

/* =========================================================
   API CONFIGURATION
========================================================= */

const API_BASE = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "")
  : "https://apimyshop.myidealtd.co.uk";

/* =========================================================
   DELIVERY CONFIGURATION
========================================================= */

const DELIVERY_COST = 5.99;

/*
  If you later want free delivery, simply change this to:

  const DELIVERY_COST = 0;
*/

/* =========================================================
   FALLBACK IMAGE
========================================================= */

const FALLBACK_IMAGE = "/images/no-image.png";

/* =========================================================
   GET IMAGE URL
========================================================= */

const getImageUrl = (item) => {
  console.log("=================================");
  console.log("SHOPPING CART IMAGE DEBUG");
  console.log("Cart item:", item);
  console.log("Cart images:", item && item.images);
  console.log("API BASE:", API_BASE);
  console.log("=================================");

  if (!item) {
    return FALLBACK_IMAGE;
  }

  let image = null;

  /* =======================================================
     1. PRODUCT IMAGES ARRAY
  ======================================================= */

  if (Array.isArray(item.images) && item.images.length > 0) {
    const firstImage = item.images[0];

    console.log("First cart image:", firstImage);

    /* -----------------------------------------------------
       IMAGE OBJECT
    ----------------------------------------------------- */

    if (firstImage && typeof firstImage === "object") {
      image =
        firstImage.cart ||
        firstImage.main ||
        firstImage.original ||
        firstImage.thumbnail ||
        firstImage.url ||
        firstImage.path ||
        firstImage.filename ||
        firstImage.name ||
        null;
    }

    /* -----------------------------------------------------
       IMAGE STRING
    ----------------------------------------------------- */

    else if (typeof firstImage === "string") {
      image = firstImage;
    }
  }

  /* =======================================================
     2. DIRECT IMAGE PROPERTY
  ======================================================= */

  if (!image) {
    image =
      item.image ||
      item.photo ||
      item.imageUrl ||
      item.imageURL ||
      null;
  }

  /* =======================================================
     3. NESTED PRODUCT
  ======================================================= */

  if (!image && item.product) {
    const product = item.product;

    if (Array.isArray(product.images) && product.images.length > 0) {
      const firstImage = product.images[0];

      if (firstImage && typeof firstImage === "object") {
        image =
          firstImage.cart ||
          firstImage.main ||
          firstImage.original ||
          firstImage.thumbnail ||
          firstImage.url ||
          firstImage.path ||
          firstImage.filename ||
          firstImage.name ||
          null;
      } else if (typeof firstImage === "string") {
        image = firstImage;
      }
    }

    if (!image) {
      image =
        product.image ||
        product.photo ||
        product.imageUrl ||
        product.imageURL ||
        null;
    }
  }

  /* =======================================================
     4. NO IMAGE
  ======================================================= */

  if (!image) {
    console.warn("No product image found in cart item.");
    return FALLBACK_IMAGE;
  }

  /* =======================================================
     5. ENSURE STRING
  ======================================================= */

  if (typeof image !== "string") {
    return FALLBACK_IMAGE;
  }

  image = image.trim();

  if (!image) {
    return FALLBACK_IMAGE;
  }

  console.log("Raw image path:", image);

  /* =======================================================
     6. FULL HTTP URL
  ======================================================= */

  if (
    image.indexOf("http://") === 0 ||
    image.indexOf("https://") === 0
  ) {
    console.log("Final image URL:", image);
    return image;
  }

  /* =======================================================
     7. /uploads/...
  ======================================================= */

  if (image.indexOf("/uploads") === 0) {
    const finalUrl = API_BASE + image;

    console.log("Final image URL:", finalUrl);

    return finalUrl;
  }

  /* =======================================================
     8. uploads/...
  ======================================================= */

  if (image.indexOf("uploads/") === 0) {
    const finalUrl = API_BASE + "/" + image;

    console.log("Final image URL:", finalUrl);

    return finalUrl;
  }

  /* =======================================================
     9. OTHER ABSOLUTE PATH
  ======================================================= */

  if (image.charAt(0) === "/") {
    const finalUrl = API_BASE + image;

    console.log("Final image URL:", finalUrl);

    return finalUrl;
  }

  /* =======================================================
     10. FILENAME ONLY
  ======================================================= */

  const finalUrl = API_BASE + "/uploads/" + image;

  console.log("Final image URL:", finalUrl);

  return finalUrl;
};

/* =========================================================
   SHOPPING CART
========================================================= */

const ShoppingCart = () => {
  /* =======================================================
     CART CONTEXT
  ======================================================= */

  const {
    cart = [],
    removeFromCart,
    increaseQuantity,
    decreaseQuantity,
  } = useCart();

  /* =======================================================
     IMAGE LOADING
  ======================================================= */

  const [imageLoading, setImageLoading] = useState({});

  /* =======================================================
     USER
  ======================================================= */

  const user = useMemo(() => {
    try {
      const storedUser = localStorage.getItem("user");

      if (!storedUser) {
        return null;
      }

      return JSON.parse(storedUser);
    } catch (error) {
      console.error("Unable to read user:", error);
      return null;
    }
  }, []);

  /* =======================================================
     ADMIN
  ======================================================= */

  const isAdmin =
    user &&
    (
      user.role === "admin" ||
      user.email === "admin" ||
      user.email === "testadmin"
    );

  /* =======================================================
     SAFE CART
  ======================================================= */

  const safeCart = Array.isArray(cart) ? cart : [];

  /* =======================================================
     TOTAL ITEMS
  ======================================================= */

  const totalItems = useMemo(() => {
    return safeCart.reduce((total, item) => {
      const quantity = Number(item && item.quantity) || 0;

      return total + quantity;
    }, 0);
  }, [safeCart]);

  /* =======================================================
     SUBTOTAL
  ======================================================= */

  const subtotal = useMemo(() => {
    return safeCart.reduce((total, item) => {
      const price = Number(item && item.price) || 0;
      const quantity = Number(item && item.quantity) || 0;

      return total + price * quantity;
    }, 0);
  }, [safeCart]);

  /* =======================================================
     DELIVERY COST
  ======================================================= */

  const deliveryCost = useMemo(() => {
    /*
      No delivery charge when cart is empty.
    */

    if (safeCart.length === 0) {
      return 0;
    }

    return DELIVERY_COST;
  }, [safeCart]);

  /* =======================================================
     GRAND TOTAL
  ======================================================= */

  const grandTotal = useMemo(() => {
    return subtotal + deliveryCost;
  }, [subtotal, deliveryCost]);

  /* =======================================================
     IMAGE LOAD
  ======================================================= */

  const handleImageLoad = (productId) => {
    setImageLoading((previous) => {
      return {
        ...previous,
        [productId]: false,
      };
    });
  };

  /* =======================================================
     IMAGE ERROR
  ======================================================= */

  const handleImageError = (event, productId) => {
    console.error(
      "SHOPPING CART IMAGE ERROR:",
      event.currentTarget.src
    );

    setImageLoading((previous) => {
      return {
        ...previous,
        [productId]: false,
      };
    });

    /*
      Prevent infinite fallback loop.
    */

    if (event.currentTarget.dataset.fallback === "true") {
      return;
    }

    event.currentTarget.dataset.fallback = "true";

    event.currentTarget.src = FALLBACK_IMAGE;
  };

  /* =======================================================
     ADMIN RESTRICTION
  ======================================================= */

  if (isAdmin) {
    return (
      <Container
        className="py-5"
        style={{
          minHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          className="border-0 text-center"
          style={{
            width: "100%",
            maxWidth: "520px",
            borderRadius: "28px",
            padding: "50px 30px",
            boxShadow: "0 20px 60px rgba(0,0,0,.10)",
          }}
        >
          <div
            style={{
              width: "90px",
              height: "90px",
              borderRadius: "50%",
              background:
                "linear-gradient(135deg,#f1f5f9,#e2e8f0)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 25px",
            }}
          >
            <FaShoppingCart
              size={38}
              className="text-muted"
            />
          </div>

          <h3 className="fw-bold mb-3">
            Access Restricted
          </h3>

          <p className="text-muted mb-0">
            Admin users are not allowed to access
            the shopping cart.
          </p>
        </Card>
      </Container>
    );
  }

  /* =======================================================
     EMPTY CART
  ======================================================= */

  if (safeCart.length === 0) {
    return (
      <Container
        className="py-5"
        style={{
          minHeight: "70vh",
        }}
      >
        <div className="text-center py-5">
          <div
            style={{
              width: "110px",
              height: "110px",
              borderRadius: "50%",
              background:
                "linear-gradient(135deg,#eff6ff,#dbeafe)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 30px",
            }}
          >
            <FaShoppingCart
              size={45}
              color="#2563eb"
            />
          </div>

          <h2 className="fw-bold mb-3">
            Your cart is empty
          </h2>

          <p
            className="text-muted mb-4"
            style={{
              fontSize: "17px",
            }}
          >
            You haven't added any products
            to your shopping cart yet.
          </p>

          <Button
            href="/shop"
            style={{
              border: "none",
              borderRadius: "999px",
              padding: "13px 30px",
              background:
                "linear-gradient(135deg,#2563eb,#3b82f6)",
              fontWeight: 700,
            }}
          >
            <FaArrowLeft className="me-2" />
            Continue Shopping
          </Button>
        </div>
      </Container>
    );
  }

  /* =======================================================
     MAIN CART
  ======================================================= */

  return (
    <>
      <Container
        fluid
        className="py-4 py-md-5"
        style={{
          minHeight: "70vh",
          paddingBottom: "130px",
        }}
      >
        {/* =================================================
            HEADER
        ================================================= */}

        <div
          className="mb-4"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "15px",
          }}
        >
          <div>
            <h2
              className="fw-bold mb-1"
              style={{
                letterSpacing: "-0.5px",
              }}
            >
              Shopping Cart
            </h2>

            <p className="text-muted mb-0">
              {totalItems}{" "}
              {totalItems === 1 ? "item" : "items"}{" "}
              in your cart
            </p>
          </div>

          <Button
            href="/shop"
            variant="outline-dark"
            style={{
              borderRadius: "999px",
              padding: "9px 20px",
              fontWeight: 600,
            }}
          >
            <FaArrowLeft className="me-2" />
            Continue Shopping
          </Button>
        </div>

        <Row className="g-4">
          {/* =================================================
              CART ITEMS
          ================================================= */}

          <Col lg={8}>
            {safeCart.map((item, index) => {
              /* PRODUCT ID */

              const productId =
                (item &&
                  (item._id || item.productId)) ||
                "cart-item-" + index;

              /* IMAGE */

              const imageUrl = getImageUrl(item);

              /* PRICE */

              const price =
                Number(item && item.price) || 0;

              /* QUANTITY */

              const quantity =
                Number(item && item.quantity) || 1;

              /* ITEM TOTAL */

              const itemTotal =
                price * quantity;

              /* LOADING */

              const isLoading =
                imageLoading[productId] !== false;

              return (
                <Card
                  key={productId}
                  className="mb-3 border-0"
                  style={{
                    borderRadius: "22px",
                    overflow: "hidden",
                    boxShadow:
                      "0 10px 35px rgba(0,0,0,.07)",
                  }}
                >
                  <Card.Body className="p-3 p-md-4">
                    <Row className="align-items-center g-3">
                      {/* =================================
                          IMAGE
                      ================================= */}

                      <Col xs={4} sm={3} md={3}>
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            height: "120px",
                            background: "#f8fafc",
                            borderRadius: "16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            border:
                              "1px solid #eef2f7",
                          }}
                        >
                          {isLoading && (
                            <div
                              style={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 1,
                                background: "#f8fafc",
                              }}
                            >
                              <Spinner
                                animation="border"
                                size="sm"
                                variant="primary"
                              />
                            </div>
                          )}

                          <img
                            src={imageUrl}
                            alt={
                              item &&
                              (
                                item.name ||
                                (
                                  item.product &&
                                  item.product.name
                                ) ||
                                "Product"
                              )
                            }
                            loading="lazy"
                            onLoad={() =>
                              handleImageLoad(
                                productId
                              )
                            }
                            onError={(event) =>
                              handleImageError(
                                event,
                                productId
                              )
                            }
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              padding: "10px",
                              display: "block",
                            }}
                          />
                        </div>
                      </Col>

                      {/* =================================
                          PRODUCT DETAILS
                      ================================= */}

                      <Col xs={8} sm={5} md={4}>
                        <h6
                          className="fw-bold mb-2"
                          style={{
                            lineHeight: 1.4,
                          }}
                        >
                          {
                            item &&
                            (
                              item.name ||
                              (
                                item.product &&
                                item.product.name
                              ) ||
                              "Product"
                            )
                          }
                        </h6>

                        <div className="text-muted mb-2">
                          £{price.toFixed(2)}
                        </div>

                        <Badge
                          bg="light"
                          text="dark"
                          style={{
                            borderRadius: "999px",
                            padding: "6px 10px",
                            fontWeight: 500,
                          }}
                        >
                          In Cart
                        </Badge>
                      </Col>

                      {/* =================================
                          QUANTITY
                      ================================= */}

                      <Col xs={6} sm={4} md={3}>
                        <div
                          className="d-flex align-items-center"
                          style={{
                            gap: "8px",
                          }}
                        >
                          <Button
                            size="sm"
                            variant="outline-dark"
                            onClick={() =>
                              decreaseQuantity(
                                productId
                              )
                            }
                            disabled={quantity <= 1}
                            style={{
                              width: "34px",
                              height: "34px",
                              padding: 0,
                              borderRadius: "10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FaMinus size={11} />
                          </Button>

                          <span
                            style={{
                              minWidth: "32px",
                              textAlign: "center",
                              fontWeight: 700,
                            }}
                          >
                            {quantity}
                          </span>

                          <Button
                            size="sm"
                            variant="outline-dark"
                            onClick={() =>
                              increaseQuantity(
                                productId
                              )
                            }
                            style={{
                              width: "34px",
                              height: "34px",
                              padding: 0,
                              borderRadius: "10px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FaPlus size={11} />
                          </Button>
                        </div>
                      </Col>

                      {/* =================================
                          TOTAL
                      ================================= */}

                      <Col
                        xs={6}
                        sm={12}
                        md={2}
                        className="text-md-end"
                      >
                        <div
                          className="fw-bold mb-2"
                          style={{
                            fontSize: "17px",
                          }}
                        >
                          £{itemTotal.toFixed(2)}
                        </div>

                        <Button
                          variant="link"
                          className="text-danger p-0"
                          onClick={() =>
                            removeFromCart(
                              productId
                            )
                          }
                          style={{
                            textDecoration: "none",
                            fontSize: "13px",
                          }}
                        >
                          <FaTrash className="me-1" />
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              );
            })}
          </Col>

          {/* =================================================
              ORDER SUMMARY
          ================================================= */}

          <Col lg={4}>
            <Card
              className="border-0"
              style={{
                borderRadius: "24px",
                boxShadow:
                  "0 15px 45px rgba(0,0,0,.09)",
                position: "sticky",
                top: "90px",
              }}
            >
              <Card.Body className="p-4">
                <h5 className="fw-bold mb-4">
                  Order Summary
                </h5>

                {/* =================================================
                    SUBTOTAL
                ================================================= */}

                <div className="d-flex justify-content-between mb-3">
                  <span className="text-muted">
                    Subtotal
                  </span>

                  <span className="fw-semibold">
                    £{subtotal.toFixed(2)}
                  </span>
                </div>

                {/* =================================================
                    DELIVERY
                ================================================= */}

                <div className="d-flex justify-content-between mb-3">
                  <span className="text-muted">
                    Delivery
                  </span>

                  <span className="fw-semibold">
                    £{deliveryCost.toFixed(2)}
                  </span>
                </div>

                <hr />

                {/* =================================================
                    TOTAL
                ================================================= */}

                <div
                  className="d-flex justify-content-between align-items-center mb-4"
                >
                  <span
                    className="fw-bold"
                    style={{
                      fontSize: "18px",
                    }}
                  >
                    Total
                  </span>

                  <span
                    className="fw-bold"
                    style={{
                      fontSize: "24px",
                      color: "#16a34a",
                    }}
                  >
                    £{grandTotal.toFixed(2)}
                  </span>
                </div>

                {/* =================================================
                    PAY BUTTON
                ================================================= */}

                <PayButton
                  cart={safeCart}
                  subtotal={subtotal}
                  deliveryCost={deliveryCost}
                  total={grandTotal}
                />

                {/* =================================================
                    SECURITY
                ================================================= */}

                <div
                  className="mt-4 pt-3"
                  style={{
                    borderTop:
                      "1px solid #eef2f7",
                  }}
                >
                  <div className="d-flex align-items-center mb-3">
                    <FaShieldAlt className="text-success me-3" />

                    <small className="text-muted">
                      Secure checkout
                    </small>
                  </div>

                  <div className="d-flex align-items-center mb-3">
                    <FaTruck className="text-primary me-3" />

                    <small className="text-muted">
                      Delivery £
                      {deliveryCost.toFixed(2)}
                    </small>
                  </div>

                  <div className="d-flex align-items-center">
                    <FaCreditCard className="text-dark me-3" />

                    <small className="text-muted">
                      Secure payment
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* =====================================================
          MOBILE CHECKOUT BAR
      ===================================================== */}

      <div
        className="d-lg-none"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "#ffffff",
          borderTop: "1px solid #e5e7eb",
          padding: "12px 16px",
          zIndex: 1000,
          boxShadow:
            "0 -8px 30px rgba(0,0,0,.10)",
        }}
      >
        <div
          className="d-flex justify-content-between align-items-center mb-2"
        >
          <span className="fw-semibold">
            Total
          </span>

          <span
            className="fw-bold"
            style={{
              fontSize: "20px",
              color: "#16a34a",
            }}
          >
            £{grandTotal.toFixed(2)}
          </span>
        </div>

        <PayButton
          cart={safeCart}
          subtotal={subtotal}
          deliveryCost={deliveryCost}
          total={grandTotal}
        />
      </div>
    </>
  );
};

export default ShoppingCart;